import { Box, Button, Stack, Tooltip } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { AutoAwesome } from '@mui/icons-material';
import { useRecoilValue } from 'recoil';
import {
    ContouringCustomizationFile,
    UserCustomization,
} from '~/backend/types';
import AvailableModels from '~/components/ModelSelection/AvailableModels';
import CustomizationSelection from '~/components/ModelSelection/CustomizationSelection';
import ModelParameters from '~/components/ModelSelection/ModelParameters';
import PseudonymizeCheckbox from '~/components/ModelSelection/PseudonymizeCheckbox';
import useModalityCompatibleModels from '~/components/ModelSelection/useModalityCompatibleModels';
import { Modality } from '~/domain/dicom/modality';
import { sessionDataAtom } from '~/state/atoms/sessionAtoms';
import { currentStructureSetState } from '~/state/atoms/structureSet/structureSetAtoms';
import { WorkflowTypes } from '~/state/atoms/viewerAtoms';
import { PredictionModel } from '~/utils/transforms/predictionModelsDataTransforms';
import StructureSelection from '../../contour/AutoSegmentation/StructureSelect';

type ContourPropagationModelSelectionProps = {
    modelCustomizations?: UserCustomization<ContouringCustomizationFile>[];
    availableModels?: PredictionModel[];
    modality?: Modality;
    disabled?: boolean;
    isLoadingModels?: boolean;
    isSubmitting?: boolean;
    showPseudonymization?: boolean;
    onSubmit: (
        model: PredictionModel,
        selectedStructures: string[],
        selectedCustomization?: string
    ) => void;
};

export default function ContourPropagationModelSelection({
    availableModels = [],
    modality,
    disabled = false,
    isLoadingModels,
    isSubmitting,
    showPseudonymization,
    onSubmit,
}: ContourPropagationModelSelectionProps) {
    const [selectedModel, setSelectedModel] = useState<PredictionModel>();
    const [selectedCustomization, setSelectedCustomization] =
        useState<string>();
    const currentStructureSet = useRecoilValue(currentStructureSetState);
    const sessionData = useRecoilValue(sessionDataAtom);

    const {
        compatibleModels,
        emptyMessage,
        labelAdornment,
        isSelectedModelCompatible,
    } = useModalityCompatibleModels(
        availableModels,
        modality,
        selectedModel,
        setSelectedModel
    );

    const roiNames = useMemo(() => {
        if (currentStructureSet?.roiArray) {
            return currentStructureSet.roiArray.map((roi) => roi.name);
        }

        const sessionStructures =
            sessionData?.payload?.context_structure_set?.structures;

        if (sessionStructures?.length) {
            return sessionStructures
                .filter((s) => !s.is_empty)
                .map((s) => s.name);
        }

        return [];
    }, [currentStructureSet, sessionData]);

    const [selectedStructures, setSelectedStructures] =
        useState<string[]>(roiNames);

    const customizations = useMemo(() => {
        return (
            selectedModel?.customizations?.filter(
                (customization) => customization.name !== selectedModel.name
            ) || []
        );
    }, [selectedModel]);

    const hasCustomizations = !!(selectedModel && customizations.length > 0);

    useEffect(() => {
        if (selectedModel) {
            setSelectedCustomization(undefined);
            setSelectedStructures(roiNames);
        }
    }, [selectedModel, roiNames]);

    const isMissingStructureSet =
        !!selectedModel && selectedStructures.length === 0;
    const isStartDisabled =
        !selectedModel ||
        disabled ||
        isMissingStructureSet ||
        !!isSubmitting ||
        !isSelectedModelCompatible;
    const startTooltipMessageId: string | undefined = !selectedModel
        ? 'selectAModelToStart'
        : isMissingStructureSet
          ? 'modelRequiresStructureSet'
          : undefined;

    return (
        <Box
            display="grid"
            gridTemplateRows="240px auto"
            gap={1}
            sx={{ transition: '0.6s' }}
        >
            <Box
                width="100%"
                height="100%"
                display="grid"
                gridTemplateColumns={
                    hasCustomizations ? '50% 25% 1fr' : '75% 1fr'
                }
                gap={1}
            >
                <AvailableModels
                    isLoading={isLoadingModels}
                    models={compatibleModels}
                    selectedModel={selectedModel}
                    onSelectedModelChanged={setSelectedModel}
                    emptyMessage={emptyMessage}
                    labelAdornment={labelAdornment}
                />
                {hasCustomizations && (
                    <CustomizationSelection
                        customizations={customizations}
                        selectedCustomization={selectedCustomization}
                        onSelectedCustomizationChanged={
                            setSelectedCustomization
                        }
                    />
                )}
                <StructureSelection
                    selectedModel={selectedModel}
                    selectedStructures={selectedStructures}
                    roiNames={roiNames}
                    setSelectedStructures={setSelectedStructures}
                    workflowType={WorkflowTypes.ADAPT}
                />
            </Box>

            <Box>
                <Box
                    mt={0.5}
                    display="flex"
                    justifyContent="space-between"
                    gap={0.5}
                >
                    <ModelParameters
                        selectedModel={selectedModel}
                        selectedCustomization={selectedCustomization}
                        selectedStructures={selectedStructures}
                        roiNames={roiNames}
                    />
                    <Stack>
                        {showPseudonymization && <PseudonymizeCheckbox />}
                        <Tooltip
                            title={
                                startTooltipMessageId ? (
                                    <FormattedMessage
                                        id={startTooltipMessageId}
                                    />
                                ) : (
                                    ''
                                )
                            }
                            placement="top"
                        >
                            {/* Wrapper needed for MUI Tooltip to work on disabled buttons */}
                            <Box component="span">
                                <Button
                                    onClick={() =>
                                        selectedModel &&
                                        onSubmit(
                                            selectedModel,
                                            selectedStructures,
                                            selectedCustomization
                                        )
                                    }
                                    variant="contained"
                                    color="primary"
                                    endIcon={<AutoAwesome />}
                                    disabled={isStartDisabled}
                                    sx={{ width: 160 }}
                                    data-testid="start-contour-propagation-button"
                                >
                                    <FormattedMessage id="start" />
                                </Button>
                            </Box>
                        </Tooltip>
                    </Stack>
                </Box>
            </Box>
        </Box>
    );
}
